import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DollarSign, Calendar } from 'lucide-react';

const PaymentModeDialog = ({ 
  open, 
  onOpenChange, 
  invoice, 
  paymentMode, 
  onPaymentModeChange,
  paidDate,
  onPaidDateChange,
  paymentMemo,
  onPaymentMemoChange,
  paymentAmount,
  onPaymentAmountChange,
  onConfirm 
}) => {
  if (!invoice) return null;

  // Default to today's date if no date provided
  const defaultDate = new Date().toISOString().split('T')[0];
  const currentPaidDate = paidDate || defaultDate;
  
  // Calculate remaining balance
  const grandTotal = invoice.grandTotal || 0;
  const paidSoFar = invoice.paidAmount || 0;
  const remainingBalance = grandTotal - paidSoFar;
  
  // Default payment amount to remaining balance
  const currentPaymentAmount = paymentAmount !== undefined && paymentAmount !== '' 
    ? paymentAmount 
    : remainingBalance;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            Record Payment
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <p className="text-gray-600">
            Record payment for invoice <strong>#{invoice.invoiceNumber}</strong>
          </p>
          
          {/* Invoice Summary */}
          <div className="bg-gray-50 p-3 rounded-lg space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Invoice Total:</span>
              <span className="font-medium">${grandTotal.toFixed(2)}</span>
            </div>
            {paidSoFar > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Already Paid:</span>
                <span className="font-medium text-green-600">${paidSoFar.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm border-t pt-1 mt-1">
              <span className="text-gray-500">Balance Due:</span>
              <span className="font-bold text-lg">${remainingBalance.toFixed(2)}</span>
            </div>
          </div>

          {/* Payment Amount */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              <DollarSign className="h-4 w-4" />
              Payment Amount
            </Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              max={remainingBalance}
              value={currentPaymentAmount}
              onChange={(e) => onPaymentAmountChange(parseFloat(e.target.value) || 0)}
              placeholder={remainingBalance.toFixed(2)}
              data-testid="payment-amount-input"
            />
            <p className="text-xs text-gray-500">
              Enter amount to pay. Leave as is for full payment.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Payment Mode</Label>
              <Select value={paymentMode} onValueChange={onPaymentModeChange}>
                <SelectTrigger data-testid="payment-mode-select">
                  <SelectValue placeholder="Select payment mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="etransfer">E-Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Payment Date
              </Label>
              <Input
                type="date"
                value={currentPaidDate}
                onChange={(e) => onPaidDateChange(e.target.value)}
                data-testid="payment-date-input"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Payment Memo (Optional)</Label>
            <Textarea
              placeholder="Add any notes about this payment..."
              value={paymentMemo || ''}
              onChange={(e) => onPaymentMemoChange(e.target.value)}
              className="h-16 resize-none"
              data-testid="payment-memo-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onConfirm} className="bg-green-600 hover:bg-green-700" data-testid="confirm-paid-btn">
            Record Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModeDialog;
